import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import WhatsAppFloat from "../components/WhatsAppFloat";
import { getCheckoutStatus } from "../lib/api";

const POLL_INTERVAL = 2500;
const MAX_ATTEMPTS = 12;

const BookingSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const awaitingApproval = params.get("awaiting_approval") === "1";
  const [status, setStatus] = useState(awaitingApproval ? "awaiting_approval" : "checking");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (awaitingApproval) return; // no polling for partner requests
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let stop = false;

    const poll = async () => {
      if (stop) return;
      try {
        const r = await getCheckoutStatus(sessionId);
        setBooking(r.booking);
        if (r.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (r.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("error");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("error");
        } else {
          setTimeout(poll, POLL_INTERVAL);
        }
      }
    };
    poll();
    return () => {
      stop = true;
    };
  }, [sessionId]);

  return (
    <div data-testid="booking-success-page">
      <Navigation />
      <div className="min-h-screen pt-32 pb-20 bg-background">
        <div className="container-x">
          <div className="max-w-2xl mx-auto bg-white border border-border p-10 md:p-14 text-center">
            {status === "awaiting_approval" && (
              <>
                <CheckCircle2 size={48} className="text-secondary mx-auto" data-testid="awaiting-approval-icon" />
                <div className="overline mt-6">Request received</div>
                <h1 className="font-serif text-4xl md:text-5xl text-primary mt-3 text-balance">
                  We&rsquo;ve got your partner-charter request.
                </h1>
                <p className="text-muted-foreground mt-4 leading-relaxed">
                  No payment has been taken. We&rsquo;ll check availability with the boat owner and email you a secure payment
                  link — usually within 24 hours. If they&rsquo;re not free for your date, we&rsquo;ll suggest alternatives
                  and no charge is ever made.
                </p>
                <Link to="/" className="btn-primary mt-8">Back to home</Link>
              </>
            )}
            {status === "checking" && (
              <>
                <Loader2 size={42} className="text-secondary mx-auto animate-spin" />
                <h1 className="font-serif text-3xl md:text-4xl text-primary mt-6">Confirming your payment…</h1>
                <p className="text-muted-foreground mt-3">This usually takes a few seconds.</p>
              </>
            )}
            {status === "paid" && (
              <>
                <CheckCircle2 size={48} className="text-secondary mx-auto" />
                <div className="overline mt-6">Booking Confirmed</div>
                <h1 className="font-serif text-4xl md:text-5xl text-primary mt-3 text-balance">
                  Thank you, your charter is booked.
                </h1>
                <p className="text-muted-foreground mt-4 leading-relaxed">
                  We&rsquo;ve emailed your confirmation. Our skipper will be in touch the day before with the exact
                  pontoon details and weather check.
                </p>
                {booking && (
                  <div className="mt-8 text-left bg-muted/40 p-6 text-sm">
                    <div className="flex justify-between py-1.5">
                      <span className="text-muted-foreground">Charter</span>
                      <span className="font-medium text-primary">{booking.charter_title}</span>
                    </div>
                    <div className="flex justify-between py-1.5">
                      <span className="text-muted-foreground">Date</span>
                      <span className="font-medium text-primary">{booking.booking_date}</span>
                    </div>
                    <div className="flex justify-between py-1.5">
                      <span className="text-muted-foreground">Guests</span>
                      <span className="font-medium text-primary">{booking.guests}</span>
                    </div>
                    <div className="flex justify-between py-1.5">
                      <span className="text-muted-foreground">Paid</span>
                      <span className="font-medium text-primary">€{Number(booking.amount_due).toLocaleString()}</span>
                    </div>
                  </div>
                )}
                <Link to="/" className="btn-primary mt-8">Back to home</Link>
              </>
            )}
            {(status === "expired" || status === "error") && (
              <>
                <AlertCircle size={42} className="text-destructive mx-auto" />
                <h1 className="font-serif text-3xl md:text-4xl text-primary mt-6">
                  We couldn&rsquo;t confirm your payment
                </h1>
                <p className="text-muted-foreground mt-3">
                  Please refresh this page in a moment, or contact us via WhatsApp and we&rsquo;ll sort it.
                </p>
                <Link to="/book" className="btn-outline mt-6">Try again</Link>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default BookingSuccessPage;
